import { createBrowserRouter, Navigate } from 'react-router-dom';
import { TutorialPages } from '../../common/src/model.ts';
import { LearnPages, Pages } from '../../common/src/pages';
import { ensureUnreachable } from '../../common/src/util.ts';
import { localStorageGet, LocalStorageKey } from './data/client_local_storage.ts';
import './index.css';
import { MostRecentFooterTabRedirect, onboardingRoutes } from './onboarding_routes.tsx';
import { AudioLabPage } from './pages/admin/engineering/audio_lab/audio_lab_page.tsx';
import { EngineeringPage } from './pages/admin/engineering/engineering_page';
import { EngineeringManageHouseholdsPage } from './pages/admin/engineering/households/engineering_manage_households_page.tsx';
import { EngineeringBrokenSongsPage } from './pages/admin/engineering/music/engineering_broken_songs_page';
import { EngineeringCustomMusicPage } from './pages/admin/engineering/music/engineering_custom_music_page';
import { HuddlePage } from './pages/admin/huddle/huddle_page.tsx';
import { Dashboard } from './pages/admin/org/dashboard/dashboard.tsx';
import { EventCalendar } from './pages/admin/org/event_calendar';
import { Planner } from './pages/admin/org/planner.tsx';
import { SeasonalCalendar } from './pages/admin/seasonal_calendar.tsx';
import { EventIsland } from './pages/event_island.tsx';
import { FailurePage } from './pages/failure_page';
import { FavoritesPage } from './pages/favorites_page';
import { HelpPage } from './pages/help_page';
import { IntervalTrainingPage } from './pages/learn/interval/interval_training_page.tsx';
import { LearnClassPage } from './pages/learn/learn_class_page.tsx';
import { LearnClassId } from './pages/learn/learn_classes.ts';
import { LearnPage } from './pages/learn/learn_page.tsx';
import { PitchMatchPage } from './pages/learn/pitch/pitch_match_page.tsx';
import { RangeFinderPage } from './pages/learn/range/range_finder_page.tsx';
import { RhythmEchoPage } from './pages/learn/rhythm/rhythm_echo_page.tsx';
import { SingRhythmPage } from './pages/learn/rhythm/sing_rhythm_page.tsx';
import { SingInSolfegePage } from './pages/learn/solfege/sing_in_solfege_page.tsx';
import { LibraryPage } from './pages/library_page';
import { LoadingPage } from './pages/loading';
import { OrgHymnalPage } from './pages/org_hymnal_page.tsx';
import { OrgPage } from './pages/org_page.tsx';
import { PrivacyPage } from './pages/privacy_page';
import { AddMemberPage } from './pages/profile_drawer/add_member_page.tsx';
import { reopenProfileDrawerAfterNavigation } from './pages/profile_drawer/profile_drawer_state.ts';
import { Root } from './pages/root_page';
import { AboutPage } from './pages/settings/about_page.tsx';
import { HouseholdPage } from './pages/settings/household_page.tsx';
import { TodayPage } from './pages/today/today_page';
import { FamilyWorshipTutorial } from './pages/tutorials/family_worship.tsx';
import { Tutorials } from './pages/tutorials/tutorials.tsx';
import { getUrlForPage, orgHymnalSubPage } from './util/path.ts';

// The Settings tab was replaced by the profile drawer (#3356); stored /settings links
// and old post-sign-out redirects land on the most recent footer tab with the drawer open.
// The reopen flag is set during render (idempotent module state) because <Navigate>
// unmounts this component before a useEffect would run.
const SettingsRedirect = () => {
  reopenProfileDrawerAfterNavigation();
  return <Navigate to={getUrlForPage(localStorageGet(LocalStorageKey.MostRecentFooterTab))} replace />;
};

// The Learn class screens and exercises behind their sub-paths (issues #3470, #3719)
function learnPageElement(page: LearnPages) {
  switch (page) {
    case LearnPages.Range:
      return <RangeFinderPage />;
    case LearnPages.Pitch:
      return <LearnClassPage classId={LearnClassId.Pitch} />;
    case LearnPages.PitchMatch:
      return <PitchMatchPage />;
    case LearnPages.IntervalTraining:
      return <IntervalTrainingPage />;
    case LearnPages.SingInSolfege:
      return <SingInSolfegePage />;
    case LearnPages.Rhythm:
      return <LearnClassPage classId={LearnClassId.Rhythm} />;
    case LearnPages.RhythmEcho:
      return <RhythmEchoPage />;
    case LearnPages.SingRhythm:
      return <SingRhythmPage />;
    default:
      return ensureUnreachable(page);
  }
}

// provide an answer to https://stackoverflow.com/questions/70341850/react-redirect-to-login-page-when-not-authenticated
// good discussion: https://github.com/remix-run/react-router/issues/10637
// good search: https://www.google.com/search?q=%2BcreateBrowserRouter+hide+routes+behind+login+page
// (searches must include "createBrowserRouter")
export const router = createBrowserRouter([
  {
    path: '/',
    element: <Root />,
    errorElement: <FailurePage />,
    children: [
      {
        index: true,
        element: <MostRecentFooterTabRedirect />,
      },
      {
        path: '*',
        element: <MostRecentFooterTabRedirect />,
      },
      ...onboardingRoutes,
      {
        path: Pages.About,
        element: <AboutPage />,
      },
      {
        path: Pages.AddMember,
        element: <AddMemberPage />,
      },
      {
        path: Pages.EngineeringAudioLab,
        element: <AudioLabPage />,
      },
      {
        path: Pages.EngineeringBrokenSongs,
        element: <EngineeringBrokenSongsPage />,
      },
      {
        path: Pages.EngineeringCustomMusic,
        element: <EngineeringCustomMusicPage />,
      },
      {
        path: Pages.EngineeringManageHouseholds,
        element: <EngineeringManageHouseholdsPage />,
      },
      {
        path: Pages.Huddle,
        element: <HuddlePage />,
      },
      {
        path: Pages.Dashboard,
        element: <Dashboard />,
      },
      {
        path: Pages.Engineering,
        element: <EngineeringPage />,
      },
      {
        path: Pages.EventIsland,
        element: <EventIsland />,
      },
      {
        path: Pages.EventCalendar,
        element: <EventCalendar />,
      },
      {
        path: Pages.Planner,
        element: <Planner />,
      },
      {
        path: Pages.SeasonalCalendar,
        element: <SeasonalCalendar />,
      },
      {
        path: 'liturgy-planner', // backward compatibility
        element: <Planner />,
      },
      {
        path: Pages.Favorites,
        element: <FavoritesPage />,
      },
      {
        path: Pages.Help,
        element: <HelpPage />,
      },
      {
        path: Pages.Household,
        element: <HouseholdPage />,
      },
      {
        path: Pages.Library,
        element: <LibraryPage />,
      },
      {
        path: Pages.Loading,
        element: <LoadingPage />,
      },
      {
        path: Pages.Learn,
        element: <LearnPage />,
        children: Object.values(LearnPages).map((page) => ({
          path: page,
          element: learnPageElement(page),
        })),
      },
      {
        path: Pages.Tutorials,
        element: <Tutorials />,
        children: [
          {
            path: TutorialPages.FamilyWorship,
            element: <FamilyWorshipTutorial />,
          },
        ],
      },
      {
        path: Pages.Privacy,
        element: <PrivacyPage />,
      },
      {
        path: Pages.Settings,
        element: <SettingsRedirect />,
      },
      {
        path: Pages.ThisWeek,
        element: <OrgPage />,
      },
      {
        path: Pages.Organization,
        element: <OrgPage />,
      },
      {
        path: Pages.Organization + '/' + orgHymnalSubPage,
        element: <OrgHymnalPage />,
      },
      {
        path: Pages.Today,
        element: <TodayPage />,
      },
    ],
  },
]);
